import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";

/**
 * Books is admin-only. That is enforced by RLS (`admin_all` policies in
 * halco-schema.sql), not by hiding the tab — a staff user who reached this
 * screen would get empty tables rather than data.
 *
 * Most of what Books shows is derived rather than stored: income comes from
 * paid rentals, expenses from maintenance costs and payroll. Only the manual
 * adjustments live in `book_entries`.
 */

export function useBooksData() {
  const entries = useQuery({
    queryKey: ["book_entries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("book_entries")
        .select("id,kind,date,category,source,amount,notes")
        .order("date", { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const categories = useQuery({
    queryKey: ["book_categories"],
    queryFn: async () => {
      const { data, error } = await supabase.from("book_categories").select("id,kind,name").order("name");
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const employees = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("id,name,role,pay_rate,pay_type")
        .order("name");
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const payroll = useQuery({
    queryKey: ["payroll"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payroll")
        .select("id,employee_id,date,amount,notes,employees(name)")
        .order("date", { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const loans = useQuery({
    queryKey: ["loans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loans")
        .select("id,lender,purpose,orig_amount,balance,rate,payment,due_day,notes")
        .order("lender");
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const rentals = useQuery({
    queryKey: ["rentals", "books"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rentals")
        .select(
          "id,invoice_no,status,paid,paid_date,start_date,end_date,rental_charge," +
            "delivery_fee,damage_waiver,late_charge,fuel_charge,damage_charge,other_fee," +
            "deposit,total,equipment(name),customers(name)"
        );
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const maintenance = useQuery({
    queryKey: ["maintenance", "books"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("maintenance")
        .select("id,date,type,parts,labor,fuel,other,cost,performed_by,notes,equipment(name)");
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const queries = [entries, categories, employees, payroll, loans, rentals, maintenance];
  return {
    entries, categories, employees, payroll, loans, rentals, maintenance,
    isPending: queries.some((q) => q.isPending),
    error: queries.find((q) => q.error)?.error ?? null,
    refetch: () => queries.forEach((q) => q.refetch()),
  };
}

/** Generic writer for the small Books tables, which all behave identically. */
function crud(table, keys) {
  return {
    useSave() {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: async ({ id, row }) => {
          const q = id
            ? supabase.from(table).update(row).eq("id", id).select("id")
            : supabase.from(table).insert([row]).select("id");
          const { data, error } = await q;
          if (error) throw new Error(error.message);
          if (!data?.length) {
            throw new Error(
              "The database accepted the request but changed nothing. Books is " +
                "admin-only — this usually means the account is not an admin."
            );
          }
          return data[0];
        },
        onSuccess: () => keys.forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
      });
    },
    useDelete() {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: async (id) => {
          const { data, error } = await supabase.from(table).delete().eq("id", id).select("id");
          if (error) {
            if (error.code === "23503") {
              throw new Error("Something still references this record, so it cannot be deleted.");
            }
            throw new Error(error.message);
          }
          if (!data?.length) throw new Error("Nothing was deleted — Books is admin-only.");
          return id;
        },
        onSuccess: () => keys.forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
      });
    },
  };
}

export const entryCrud = crud("book_entries", ["book_entries"]);
export const employeeCrud = crud("employees", ["employees", "payroll"]);
export const payrollCrud = crud("payroll", ["payroll"]);
export const loanCrud = crud("loans", ["loans"]);

export const blankToNull = (v) => (v === "" || v === undefined ? null : v);
export const money = (v) => (v === "" || v === undefined || v === null ? 0 : Number(v) || 0);
